import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Dumbbell, Plus, X, Medal, ChevronLeft, ChevronRight } from 'lucide-react';
import { usePageTitle } from '../components/AppShell';
import { Toast, type ToastVariant } from '../components/Toast';
import { apiGet, apiPost, type AuthUser } from '../lib/auth';
import { useLang } from '../lib/lang';
import { hasRole, schoolIdFromUser } from '../lib/rbac';

const C = {
  yellow: '#F7D000', black: '#1A1A1A', white: '#FFFFFF',
  border: '#E5E7EB', gray100: '#F3F4F6', gray500: '#6B7280',
  gray700: '#374151', gray900: '#111827',
  success: '#166534', successBg: '#DCFCE7',
  error: '#991B1B', errorBg: '#FEE2E2',
  info: '#1E40AF', infoBg: '#DBEAFE',
  teal: '#0F766E', tealBg: '#CCFBF1',
};

const BN = {
  title: 'Ko-Kurikulum (CCA)', subtitle: 'rekod', back: '← Laman Utama',
  addRecord: '+ Tambah Pencapaian', tabs: ['Rekod', 'Ringkasan'] as const,
  search: 'Cari', searchPh: 'Nama, aktiviti…', allCat: 'Semua Kategori', allYear: 'Semua Tahun',
  prev: 'Sebelum', next: 'Seterusnya', showing: 'Menunjukkan', to: 'hingga', of: 'daripada',
  emptyTitle: 'Tiada Rekod CCA', emptyBody: 'Tiada pencapaian ko-kurikulum direkodkan.',
  addTitle: 'Rekod Pencapaian CCA', studentId: 'ID Pelajar *', activity: 'Aktiviti *',
  category: 'Kategori *', position: 'Jawatan/Pencapaian *', level: 'Peringkat *',
  academicYear: 'Tahun Akademik *', awardDate: 'Tarikh', description: 'Penerangan',
  save: 'Simpan', saving: 'Menyimpan…', cancel: 'Batal',
  retry: 'Cuba Lagi', errLoad: 'Gagal memuatkan.',
  totalStudents: 'Pelajar Aktif', topActivity: 'Aktiviti Terbanyak', totalRecords: 'Jumlah Rekod',
};
const EN = {
  title: 'Co-Curricular Activities (CCA)', subtitle: 'records', back: '← Home',
  addRecord: '+ Add Achievement', tabs: ['Records', 'Summary'] as const,
  search: 'Search', searchPh: 'Name, activity…', allCat: 'All Categories', allYear: 'All Years',
  prev: 'Previous', next: 'Next', showing: 'Showing', to: 'to', of: 'of',
  emptyTitle: 'No CCA Records', emptyBody: 'No co-curricular achievements recorded.',
  addTitle: 'Record CCA Achievement', studentId: 'Student ID *', activity: 'Activity *',
  category: 'Category *', position: 'Position/Achievement *', level: 'Level *',
  academicYear: 'Academic Year *', awardDate: 'Date', description: 'Description',
  save: 'Save', saving: 'Saving…', cancel: 'Cancel',
  retry: 'Retry', errLoad: 'Failed to load.',
  totalStudents: 'Active Students', topActivity: 'Top Activity', totalRecords: 'Total Records',
};

const CCA_CATEGORIES = ['SPORTS','UNIFORM_BODY','CLUBS_SOCIETIES','ARTS_CULTURE','COMMUNITY_SERVICE','ACADEMIC_COMPETITION'];
const CAT_CFG: Record<string, { ms: string; en: string; color: string; bg: string }> = {
  SPORTS:               { ms: 'Sukan',           en: 'Sports',               color: '#166534', bg: '#DCFCE7' },
  UNIFORM_BODY:         { ms: 'Badan Beruniform', en: 'Uniform Body',         color: '#1E40AF', bg: '#DBEAFE' },
  CLUBS_SOCIETIES:      { ms: 'Kelab & Persatuan',en: 'Clubs & Societies',    color: '#6D28D9', bg: '#EDE9FE' },
  ARTS_CULTURE:         { ms: 'Seni & Budaya',    en: 'Arts & Culture',       color: '#BE185D', bg: '#FCE7F3' },
  COMMUNITY_SERVICE:    { ms: 'Khidmat Masyarakat',en: 'Community Service',   color: '#0F766E', bg: '#CCFBF1' },
  ACADEMIC_COMPETITION: { ms: 'Pertandingan Akademik',en: 'Academic Competition',color: '#B45309', bg: '#FEF9C3' },
};
const LEVELS = ['SCHOOL','DISTRICT','NATIONAL','INTERNATIONAL'];
const LEVEL_CFG: Record<string, { ms: string; en: string }> = {
  SCHOOL:        { ms: 'Sekolah',       en: 'School' },
  DISTRICT:      { ms: 'Daerah',        en: 'District' },
  NATIONAL:      { ms: 'Kebangsaan',    en: 'National' },
  INTERNATIONAL: { ms: 'Antarabangsa',  en: 'International' },
};

interface Props { user: AuthUser }
interface CCARecord {
  id: string; student_id: string; activity: string; category: string;
  position: string; level: string; academic_year: string;
  award_date: string | null; description: string | null;
  full_name_en: string | null; full_name_ms: string | null; ic_number: string; class_name: string | null;
}
interface PageResult { data: CCARecord[]; total: number; page: number }
const LIMIT = 20;
const cardShadow = '0 1px 3px rgba(0,0,0,0.08)';

export function CCAPage({ user }: Props) {
  const { lang } = useLang();
  const UI = lang === 'en' ? EN : BN;
  usePageTitle(UI.title);

  const canEdit  = hasRole(user, 'super_admin', 'admin', 'principal', 'teacher', 'clerk');
  const schoolId = schoolIdFromUser(user) ?? '';

  const [tab,     setTab]     = useState<'records' | 'summary'>('records');
  const [records, setRecords] = useState<CCARecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState<string | null>(null);
  const [page,    setPage]    = useState(1);
  const [total,   setTotal]   = useState(0);
  const [search,  setSearch]  = useState('');
  const [catFilter, setCatFilter] = useState('');
  const [yearFilter, setYearFilter] = useState('');
  const [showAdd, setShowAdd] = useState(false);
  const [saving,  setSaving]  = useState(false);
  const [toast,   setToast]   = useState<{ message: string; variant: ToastVariant } | null>(null);

  const [form, setForm] = useState({
    studentId: '', activity: '', category: 'SPORTS', position: '', level: 'SCHOOL',
    academicYear: '', awardDate: '', description: '',
  });

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const p = new URLSearchParams({ schoolId, page: String(page) });
      if (search.trim())  p.set('q', search.trim());
      if (catFilter)      p.set('category', catFilter);
      if (yearFilter)     p.set('academicYear', yearFilter);
      const res = await apiGet<PageResult>(`/api/cca/records?${p}`);
      setRecords(res.data); setTotal(res.total);
    } catch (e) { setError(e instanceof Error ? e.message : UI.errLoad); }
    finally { setLoading(false); }
  }, [schoolId, page, search, catFilter, yearFilter, UI.errLoad]);

  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    if (!form.studentId.trim() || !form.activity.trim() || !form.position.trim() || !form.academicYear.trim()) return;
    setSaving(true);
    try {
      await apiPost('/api/cca/records', {
        studentId: form.studentId, activity: form.activity, category: form.category,
        position: form.position, level: form.level, academicYear: form.academicYear,
        awardDate: form.awardDate || undefined, description: form.description || undefined,
        schoolId,
      });
      setShowAdd(false);
      setForm({ studentId: '', activity: '', category: 'SPORTS', position: '', level: 'SCHOOL', academicYear: '', awardDate: '', description: '' });
      setToast({ message: lang === 'en' ? 'CCA record added.' : 'Rekod CCA ditambah.', variant: 'success' });
      load();
    } catch (e) { setToast({ message: e instanceof Error ? e.message : 'Ralat.', variant: 'error' }); }
    finally { setSaving(false); }
  };

  const inputStyle: React.CSSProperties = { width: '100%', padding: '8px 10px', borderRadius: '6px', border: `1px solid ${C.border}`, fontSize: '14px', color: C.gray900, backgroundColor: C.white, boxSizing: 'border-box' };
  const labelStyle: React.CSSProperties = { fontSize: '12px', fontWeight: 600, color: C.gray700, marginBottom: '4px', display: 'block' };

  const byCategory = CCA_CATEGORIES.map(c => ({ cat: c, count: records.filter(r => r.category === c).length })).filter(x => x.count > 0);

  return (
    <div style={{ padding: '24px', maxWidth: '1100px', margin: '0 auto' }}>
      {toast && <Toast message={toast.message} variant={toast.variant} onDismiss={() => setToast(null)} />}
      <div style={{ marginBottom: '24px' }}>
        <Link to="/" style={{ fontSize: '13px', color: C.gray500, textDecoration: 'none' }}>{UI.back}</Link>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: '8px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div style={{ width: '40px', height: '40px', borderRadius: '10px', background: C.tealBg, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Dumbbell size={20} color={C.teal} />
            </div>
            <div>
              <h1 style={{ margin: 0, fontSize: '20px', fontWeight: 700, color: C.gray900 }}>{UI.title}</h1>
              <p style={{ margin: 0, fontSize: '13px', color: C.gray500 }}>{total} {UI.subtitle}</p>
            </div>
          </div>
          {canEdit && <button onClick={() => setShowAdd(true)} style={{ padding: '9px 16px', borderRadius: '8px', background: C.yellow, border: 'none', fontWeight: 600, fontSize: '13px', cursor: 'pointer', color: C.black }}>{UI.addRecord}</button>}
        </div>
      </div>

      <div style={{ display: 'flex', gap: '4px', marginBottom: '20px', borderBottom: `1px solid ${C.border}` }}>
        {(['records', 'summary'] as const).map((t, i) => (
          <button key={t} onClick={() => setTab(t)} style={{ padding: '10px 20px', border: 'none', background: 'transparent', fontSize: '14px', fontWeight: 600, cursor: 'pointer', color: tab === t ? C.black : C.gray500, borderBottom: tab === t ? `2px solid ${C.yellow}` : '2px solid transparent', marginBottom: '-1px' }}>
            {lang === 'en' ? EN.tabs[i] : BN.tabs[i]}
          </button>
        ))}
      </div>

      {tab === 'records' && (
        <>
          <div style={{ display: 'flex', gap: '10px', marginBottom: '16px', flexWrap: 'wrap' }}>
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder={UI.searchPh}
              style={{ ...inputStyle, width: '220px' }} onKeyDown={e => { if (e.key === 'Enter') { setPage(1); load(); }}} />
            <select value={catFilter} onChange={e => { setCatFilter(e.target.value); setPage(1); }} style={{ ...inputStyle, width: '200px' }}>
              <option value="">{UI.allCat}</option>
              {CCA_CATEGORIES.map(c => <option key={c} value={c}>{lang === 'en' ? CAT_CFG[c]?.en : CAT_CFG[c]?.ms}</option>)}
            </select>
            <button onClick={() => { setPage(1); load(); }} style={{ padding: '8px 16px', borderRadius: '6px', background: C.yellow, border: 'none', fontWeight: 600, fontSize: '13px', cursor: 'pointer' }}>{UI.search}</button>
          </div>

          {loading && <div style={{ textAlign: 'center', padding: '40px', color: C.gray500 }}>…</div>}
          {error && <div style={{ padding: '16px', background: C.errorBg, color: C.error, borderRadius: '8px', marginBottom: '16px' }}>{error}</div>}

          {!loading && records.length === 0 && (
            <div style={{ textAlign: 'center', padding: '60px', color: C.gray500 }}>
              <Medal size={40} style={{ opacity: 0.3, marginBottom: '12px' }} />
              <p style={{ fontWeight: 600, margin: 0 }}>{UI.emptyTitle}</p>
            </div>
          )}

          <div style={{ display: 'grid', gap: '10px' }}>
            {records.map(r => {
              const cc = CAT_CFG[r.category];
              const lv = LEVEL_CFG[r.level];
              return (
                <div key={r.id} style={{ background: C.white, border: `1px solid ${C.border}`, borderRadius: '10px', padding: '16px', boxShadow: cardShadow, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 700, fontSize: '14px', color: C.gray900 }}>{lang === 'en' ? (r.full_name_en ?? r.ic_number) : (r.full_name_ms ?? r.full_name_en ?? r.ic_number)}</div>
                    <div style={{ fontSize: '13px', color: C.gray700, marginTop: '2px' }}>{r.activity} · {r.position}</div>
                    <div style={{ fontSize: '12px', color: C.gray500 }}>{r.academic_year}{r.class_name ? ` · ${r.class_name}` : ''}</div>
                  </div>
                  <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                    <span style={{ padding: '3px 10px', borderRadius: '20px', fontSize: '11px', fontWeight: 600, background: cc?.bg, color: cc?.color }}>{lang === 'en' ? cc?.en : cc?.ms}</span>
                    <span style={{ padding: '3px 10px', borderRadius: '20px', fontSize: '11px', fontWeight: 600, background: C.infoBg, color: C.info }}>{lang === 'en' ? lv?.en : lv?.ms}</span>
                  </div>
                </div>
              );
            })}
          </div>

          {total > LIMIT && (
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '20px', fontSize: '13px', color: C.gray500 }}>
              <span>{UI.showing} {(page - 1) * LIMIT + 1} {UI.to} {Math.min(page * LIMIT, total)} {UI.of} {total}</span>
              <div style={{ display: 'flex', gap: '8px' }}>
                <button disabled={page === 1} onClick={() => setPage(p => p - 1)} style={{ padding: '6px 14px', borderRadius: '6px', border: `1px solid ${C.border}`, background: C.white, cursor: page === 1 ? 'default' : 'pointer', opacity: page === 1 ? 0.5 : 1 }}><ChevronLeft size={14} /></button>
                <button disabled={page * LIMIT >= total} onClick={() => setPage(p => p + 1)} style={{ padding: '6px 14px', borderRadius: '6px', border: `1px solid ${C.border}`, background: C.white, cursor: page * LIMIT >= total ? 'default' : 'pointer', opacity: page * LIMIT >= total ? 0.5 : 1 }}><ChevronRight size={14} /></button>
              </div>
            </div>
          )}
        </>
      )}

      {tab === 'summary' && (
        <div style={{ display: 'grid', gap: '16px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '12px' }}>
            {[
              { label: UI.totalRecords, value: total, color: C.teal, bg: C.tealBg },
              { label: UI.totalStudents, value: new Set(records.map(r => r.student_id)).size, color: C.info, bg: C.infoBg },
            ].map(card => (
              <div key={card.label} style={{ background: card.bg, borderRadius: '10px', padding: '20px', textAlign: 'center' }}>
                <div style={{ fontSize: '32px', fontWeight: 800, color: card.color }}>{card.value}</div>
                <div style={{ fontSize: '13px', color: card.color, marginTop: '4px' }}>{card.label}</div>
              </div>
            ))}
          </div>
          <div style={{ background: C.white, border: `1px solid ${C.border}`, borderRadius: '10px', padding: '20px' }}>
            <h3 style={{ margin: '0 0 16px', fontSize: '14px', fontWeight: 700 }}>{lang === 'en' ? 'By Category' : 'Mengikut Kategori'}</h3>
            <div style={{ display: 'grid', gap: '8px' }}>
              {byCategory.map(({ cat, count }) => {
                const cfg = CAT_CFG[cat];
                const pct = Math.round((count / Math.max(1, total)) * 100);
                return (
                  <div key={cat}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', marginBottom: '4px' }}>
                      <span style={{ color: C.gray700 }}>{lang === 'en' ? cfg?.en : cfg?.ms}</span>
                      <span style={{ fontWeight: 700 }}>{count}</span>
                    </div>
                    <div style={{ height: '6px', background: C.gray100, borderRadius: '3px', overflow: 'hidden' }}>
                      <div style={{ height: '100%', width: `${pct}%`, background: cfg?.color ?? C.teal, borderRadius: '3px', transition: 'width 0.3s' }} />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {showAdd && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: C.white, borderRadius: '12px', padding: '24px', width: '500px', maxHeight: '85vh', overflowY: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '20px' }}>
              <h2 style={{ margin: 0, fontSize: '16px', fontWeight: 700 }}>{UI.addTitle}</h2>
              <button onClick={() => setShowAdd(false)} style={{ background: 'none', border: 'none', cursor: 'pointer' }}><X size={18} /></button>
            </div>
            <div style={{ display: 'grid', gap: '12px' }}>
              {[
                { key: 'studentId', label: UI.studentId, type: 'text' },
                { key: 'activity', label: UI.activity, type: 'text' },
                { key: 'position', label: UI.position, type: 'text' },
                { key: 'academicYear', label: UI.academicYear, type: 'text' },
                { key: 'awardDate', label: UI.awardDate, type: 'date' },
              ].map(f => (
                <div key={f.key}>
                  <label style={labelStyle}>{f.label}</label>
                  <input type={f.type} value={form[f.key as keyof typeof form]}
                    onChange={e => setForm(p => ({ ...p, [f.key]: e.target.value }))} style={inputStyle} />
                </div>
              ))}
              {[
                { key: 'category', label: UI.category, opts: CCA_CATEGORIES.map(c => ({ v: c, l: lang === 'en' ? CAT_CFG[c]?.en ?? c : CAT_CFG[c]?.ms ?? c })) },
                { key: 'level', label: UI.level, opts: LEVELS.map(l => ({ v: l, l: lang === 'en' ? LEVEL_CFG[l]?.en ?? l : LEVEL_CFG[l]?.ms ?? l })) },
              ].map(f => (
                <div key={f.key}>
                  <label style={labelStyle}>{f.label}</label>
                  <select value={form[f.key as keyof typeof form]} onChange={e => setForm(p => ({ ...p, [f.key]: e.target.value }))} style={inputStyle}>
                    {f.opts.map(o => <option key={o.v} value={o.v}>{o.l}</option>)}
                  </select>
                </div>
              ))}
              <div>
                <label style={labelStyle}>{UI.description}</label>
                <textarea value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))}
                  style={{ ...inputStyle, height: '60px', resize: 'vertical' }} />
              </div>
            </div>
            <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
              <button onClick={submit} disabled={saving} style={{ flex: 1, padding: '10px', borderRadius: '8px', background: C.yellow, border: 'none', fontWeight: 700, cursor: saving ? 'default' : 'pointer', opacity: saving ? 0.7 : 1 }}>
                {saving ? UI.saving : UI.save}
              </button>
              <button onClick={() => setShowAdd(false)} style={{ flex: 1, padding: '10px', borderRadius: '8px', background: C.gray100, border: `1px solid ${C.border}`, fontWeight: 600, cursor: 'pointer' }}>{UI.cancel}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
